'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'Services', href: '/services' },
    { label: 'Gallery', href: '/gallery' },
    { label: 'About', href: '/about' },
  ];

  const isActive = (href: string) => pathname === href;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || mobileOpen
          ? 'bg-[#0a0a0a]/95 backdrop-blur-sm border-b border-[#D4AF37]/20'
          : 'bg-[#0a0a0a]/95 backdrop-blur-sm border-b border-[#D4AF37]/20'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link href="/" className="flex items-center min-w-0">
            <Image
              src="/assets/images/da1mamba-logo-1774498100478.png"
              alt="DA1MAMBA car logo"
              width={112}
              height={112}
              className="w-16 h-16 sm:w-[90px] sm:h-[90px] lg:w-28 lg:h-28 object-contain flex-shrink-0"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className={`text-sm xl:text-base font-medium transition-colors duration-300 cursor-pointer tracking-wide ${
                  isActive(link.href)
                    ? 'text-[#D4AF37]'
                    : 'text-white hover:text-[#D4AF37]'
                }`}
                style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center">
            <a
              href="sms:9492935721"
              className="bg-[#D4AF37] hover:bg-white text-black font-semibold px-6 py-2 rounded-lg transition-colors duration-300 text-sm uppercase tracking-wide"
              style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}
            >
              Book a Detail
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 text-[#D4AF37] hover:bg-[#D4AF37]/20 rounded-md transition-colors"
            aria-label="Toggle menu"
          >
            {mobileOpen ? (
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <line x1="4" x2="20" y1="12" y2="12" strokeWidth={2} strokeLinecap="round" />
                <line x1="4" x2="20" y1="6" y2="6" strokeWidth={2} strokeLinecap="round" />
                <line x1="4" x2="20" y1="18" y2="18" strokeWidth={2} strokeLinecap="round" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-[#D4AF37]/20 bg-[#0a0a0a]">
          <nav className="flex flex-col gap-1 px-4 py-4">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className={`px-4 py-3 text-sm font-medium tracking-wide uppercase transition-colors duration-200 ${
                  isActive(link.href)
                    ? 'text-[#D4AF37]'
                    : 'text-white hover:text-[#D4AF37]'
                }`}
                style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}
              >
                {link.label}
              </Link>
            ))}
            <div className="flex flex-col gap-2 pt-3 border-t border-[#D4AF37]/20 mt-2">
              <a
                href="tel:+19492935721"
                className="text-center border border-[#D4AF37]/40 text-[#D4AF37] text-sm font-semibold px-5 py-3 rounded-lg transition-colors duration-200"
              >
                Call (949) 293-5721
              </a>
              <a
                href="sms:9492935721"
                className="text-center bg-[#D4AF37] hover:bg-white text-black text-sm font-semibold px-5 py-3 rounded-lg transition-colors duration-200 tracking-wide uppercase"
              >
                Book a Detail
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}